import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { hasuraClient } from '../../utils/graphqlClient';
import {
  GET_FIRMS_FOR_IMPORT_QUERY,
  GET_EXISTING_COLLECTIONS_FIRM_IDS_QUERY,
} from '../../graphql/queries/collections';
import {
  UPSERT_COLLECTIONS_MUTATION,
  DELETE_COLLECTIONS_NOT_IN_LIST_MUTATION,
} from '../../graphql/mutations/collections';
import { useAuth } from '../../hooks';
import { formatCurrency } from '../../constants/tableConfigurations';

// Parse Turkish number format: "522.440,60" → 522440.60
// If XLSX already parsed the cell as a JS number, return it directly.
const parseTRNumber = (val) => {
  if (val === undefined || val === null || val === '') return 0;
  if (typeof val === 'number') return val;
  const str = String(val).replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const CollectionImportModal = ({ open, onClose, onImported }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();

  const [step, setStep] = useState('upload'); // 'upload' | 'preview' | 'importing' | 'done'
  const [rows, setRows] = useState([]); // parsed + matched rows
  const [existingFirmIds, setExistingFirmIds] = useState([]); // collections halihazırda var olan firm_id'ler
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => {
    setStep('upload');
    setRows([]);
    setExistingFirmIds([]);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = useCallback(async (file) => {
    if (!file) return;
    try {
      // 1. Parse Excel
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      // 2. Find header row (contains "Firma Kodu" or similar)
      let headerIdx = raw.findIndex(row =>
        row.some(cell => String(cell).toLowerCase().includes('firma') || String(cell).toLowerCase().includes('kod'))
      );
      if (headerIdx === -1) headerIdx = 0;

      const headers = raw[headerIdx].map(h => String(h).trim());
      const dataRows = raw.slice(headerIdx + 1).filter(r => r.some(c => c !== ''));

      // 3. Find column indices
      const findCol = (...keywords) => {
        const idx = headers.findIndex(h =>
          keywords.some(kw => h.toLowerCase().includes(kw.toLowerCase()))
        );
        return idx;
      };

      const lucaIdx = findCol('firma kodu', 'kod', 'code');
      const borIdx = findCol('borç', 'bor');
      const alacakIdx = findCol('alacak');
      const bakiyeIdx = findCol('bakiye');

      if (lucaIdx === -1) {
        alert(t('tahsilat.import.noLucaColumn', 'Luca kodu sütunu bulunamadı'));
        return;
      }

      // 4. Fetch firms with luca_code + existing collections (silinecek sayısı için)
      const [firmsData, existingData] = await Promise.all([
        hasuraClient.query(GET_FIRMS_FOR_IMPORT_QUERY, { companyId: user.companyId }),
        hasuraClient.query(GET_EXISTING_COLLECTIONS_FIRM_IDS_QUERY, { companyId: user.companyId }),
      ]);
      const firms = firmsData.firms || [];
      const firmMap = new Map(firms.map(f => [String(f.luca_code).trim(), f]));
      setExistingFirmIds((existingData.collections || []).map(c => c.firm_id));

      // 5. Match rows
      const matched = dataRows
        .map(row => {
          const lucaCode = String(row[lucaIdx] || '').trim();
          if (!lucaCode) return null;
          const firm = firmMap.get(lucaCode) || null;
          return {
            lucaCode,
            tlBor: parseTRNumber(row[borIdx]),
            tlAlacak: parseTRNumber(row[alacakIdx]),
            tlBakiye: parseTRNumber(row[bakiyeIdx]),
            firm,
          };
        })
        .filter(Boolean);

      setRows(matched);
      setStep('preview');
    } catch (e) {
      console.error('Excel parse error:', e);
      alert(t('tahsilat.import.parseError', 'Excel dosyası okunamadı'));
    }
  }, [user, t]);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleImport = useCallback(async () => {
    const matchedRows = rows.filter(r => r.firm);
    if (!matchedRows.length) return;

    setImporting(true);
    setStep('importing');
    try {
      // Deduplicate by firm_id — same firm may appear multiple times in Excel
      // Sum the financial values across duplicate rows
      const deduped = new Map();
      matchedRows.forEach(r => {
        if (deduped.has(r.firm.id)) {
          const ex = deduped.get(r.firm.id);
          ex.tl_bor += r.tlBor;
          ex.tl_alacak += r.tlAlacak;
          ex.tl_bakiye += r.tlBakiye;
        } else {
          deduped.set(r.firm.id, {
            firm_id: r.firm.id,
            luca_code: r.lucaCode,
            tl_bor: r.tlBor,
            tl_alacak: r.tlAlacak,
            tl_bakiye: r.tlBakiye,
            import_date: new Date().toISOString(),
            company_id: user.companyId,
          });
        }
      });
      const objects = Array.from(deduped.values());
      const keepFirmIds = objects.map(o => o.firm_id);

      const data = await hasuraClient.mutate(UPSERT_COLLECTIONS_MUTATION, { objects });

      // Yüklenen Excel'de olmayan firmaların tahsilat kayıtlarını sil.
      // Hata olursa sessizce yutmuyoruz — kullanıcı upsert'in başarılı,
      // delete'in başarısız olduğunu görmeli.
      const delResult = await hasuraClient.mutate(DELETE_COLLECTIONS_NOT_IN_LIST_MUTATION, {
        companyId: user.companyId,
        keepFirmIds,
      });
      const deletedCount = delResult.delete_collections?.affected_rows || 0;

      setResult({
        affected_rows: data.insert_collections.affected_rows,
        deleted_rows: deletedCount,
      });
      setStep('done');
      onImported?.();
    } catch (e) {
      console.error('Import error:', e);
      alert(t('tahsilat.import.importError', 'Import sırasında hata oluştu'));
      setStep('preview');
    } finally {
      setImporting(false);
    }
  }, [rows, user, t, onImported]);

  const matchedCount = rows.filter(r => r.firm).length;
  const unmatchedCount = rows.filter(r => !r.firm).length;
  // Excel'de olan ama mevcutta da bulunan firma id setini çıkarıp
  // mevcuttaki diğer collection'ların silineceği sayıyı hesaplıyoruz.
  const uploadFirmIdSet = new Set(rows.filter(r => r.firm).map(r => r.firm.id));
  const toDeleteCount = existingFirmIds.filter(id => !uploadFirmIdSet.has(id)).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        {t('tahsilat.import.title', 'Luca Raporu İmport Et')}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* UPLOAD STEP */}
        {step === 'upload' && (
          <Box sx={{ p: 3 }}>
            <Box
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => document.getElementById('collection-file-input').click()}
              sx={{
                border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
                borderRadius: 3,
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                bgcolor: dragOver ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                transition: 'all 0.2s',
                '&:hover': {
                  border: `2px dashed ${theme.palette.primary.main}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                },
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 52, color: 'primary.main', opacity: 0.7 }} />
              <Typography variant="h6" fontWeight={600}>
                {t('tahsilat.import.dropHere', 'Excel dosyasını buraya sürükle veya tıkla')}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {t('tahsilat.import.fileTypes', '.xlsx, .xls formatları desteklenir')}
              </Typography>
              <input
                id="collection-file-input"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </Box>

            <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.06), border: `1px solid ${alpha(theme.palette.info.main, 0.15)}` }}>
              <Typography variant="caption" color="info.main" fontWeight={700} display="block" mb={0.5}>
                {t('tahsilat.import.expectedFormat', 'Beklenen Excel formatı')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('tahsilat.import.formatDesc', 'Firma Kodu · TL Borç · TL Alacak · TL Bakiye — Luca Cari Bakiye raporu doğrudan yüklenebilir.')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* PREVIEW STEP */}
        {step === 'preview' && (
          <Box>
            <Stack direction="row" gap={2} sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                label={`${matchedCount} ${t('tahsilat.import.matched', 'eşleşti')}`}
                color="success"
                size="small"
                sx={{ fontWeight: 700 }}
              />
              {unmatchedCount > 0 && (
                <Chip
                  icon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
                  label={`${unmatchedCount} ${t('tahsilat.import.unmatched', 'eşleşmedi')}`}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
              {toDeleteCount > 0 && (
                <Chip
                  icon={<WarningAmberIcon sx={{ fontSize: 15 }} />}
                  label={`${toDeleteCount} ${t('tahsilat.import.toDelete', 'tahsilat silinecek')}`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
              <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto', alignSelf: 'center' }}>
                {t('tahsilat.import.previewNote', 'Listede olmayan firmaların tahsilatları silinir')}
              </Typography>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('tahsilat.lucaCode', 'Luca Kodu')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('tahsilat.firmName', 'Firma')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>{t('tahsilat.tlBor', 'TL Borç')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>{t('tahsilat.tlAlacak', 'TL Alacak')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>{t('tahsilat.tlBakiye', 'TL Bakiye')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>{t('tahsilat.import.status', 'Durum')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow
                      key={i}
                      sx={{
                        bgcolor: row.firm ? 'transparent' : alpha(theme.palette.warning.main, 0.04),
                        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.02) },
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>
                        {row.lucaCode}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {row.firm
                          ? <Typography variant="caption" fontWeight={600}>{row.firm.firm_name}</Typography>
                          : <Typography variant="caption" color="text.disabled">—</Typography>
                        }
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {formatCurrency(row.tlBor)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                        {formatCurrency(row.tlAlacak)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: 12,
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          color: row.tlBakiye > 0 ? 'error.main' : row.tlBakiye < 0 ? 'success.main' : 'text.primary',
                        }}
                      >
                        {formatCurrency(row.tlBakiye)}
                      </TableCell>
                      <TableCell>
                        {row.firm
                          ? <Chip label={t('tahsilat.import.ok', 'Eşleşti')} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
                          : <Chip label={t('tahsilat.import.noFirm', 'Firma yok')} size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* IMPORTING */}
        {step === 'importing' && (
          <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <CircularProgress size={48} />
            <Typography variant="h6" fontWeight={600}>{t('tahsilat.import.importing', 'İmport ediliyor...')}</Typography>
            <LinearProgress sx={{ width: '100%', borderRadius: 1 }} />
          </Box>
        )}

        {/* DONE */}
        {step === 'done' && result && (
          <Box sx={{ p: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h6" fontWeight={700}>{t('tahsilat.import.success', 'İmport tamamlandı!')}</Typography>
            <Typography color="text.secondary">
              {result.affected_rows} {t('tahsilat.import.recordsUpdated', 'firma kaydı güncellendi.')}
            </Typography>
            {result.deleted_rows > 0 && (
              <Typography color="error.main" variant="body2">
                {result.deleted_rows} {t('tahsilat.import.recordsDeleted', 'firma tahsilatı silindi.')}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {step === 'upload' && (
          <Button onClick={handleClose} variant="text" color="inherit">
            {t('common.cancel', 'İptal')}
          </Button>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={reset} variant="text" color="inherit">
              {t('tahsilat.import.reupload', 'Farklı Dosya')}
            </Button>
            <Button onClick={handleClose} variant="text" color="inherit">
              {t('common.cancel', 'İptal')}
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={matchedCount === 0 || importing}
              sx={{ borderRadius: 2 }}
            >
              {t('tahsilat.import.confirm', `${matchedCount} Firmayı İmport Et`)}
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button onClick={handleClose} variant="contained" sx={{ borderRadius: 2 }}>
            {t('common.close', 'Kapat')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CollectionImportModal;
